// AU17 (docs/build-briefs/AU17-change-history.md; design/mockups/au17-notes.md). The Change history
// screen's decisions as pure functions: the date chips, what a filter set asks the server, the day
// headings, the words under an empty list and the footer sentence. No DOM and no session import, so a
// plain node:test file can import it (test/ui-change-history-model.test.ts) and
// ui/src/changeHistory.ts draws from it.

import type { ChangeHistoryEntryDto, ChangeHistoryPageDto } from "./api.ts";
import type { GateExportFormat } from "./exportMenuModel.ts";

export type ChangeHistoryRangeId =
  "today" | "yesterday" | "last_7_days" | "last_30_days" | "this_month" | "custom";

export const CHANGE_HISTORY_RANGE_CHIPS: readonly { id: ChangeHistoryRangeId; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last_7_days", label: "Last 7 days" },
  { id: "last_30_days", label: "Last 30 days" },
  { id: "this_month", label: "This month" },
  { id: "custom", label: "Custom dates" },
];

export interface ChangeHistoryFilters {
  range: ChangeHistoryRangeId;
  /** Only read when range is "custom": YYYY-MM-DD in the location's time zone. */
  from: string;
  to: string;
  /** "" for everyone, else a person key the server listed ("support", a user id). */
  person: string;
  /** "" for every area, else an area id. */
  area: string;
  q: string;
}

export function defaultFilters(): ChangeHistoryFilters {
  return { range: "last_30_days", from: "", to: "", person: "", area: "", q: "" };
}

/** True when a Person, Area or search filter is on (the date range always applies). */
export function filtersActive(filters: ChangeHistoryFilters): boolean {
  return filters.person !== "" || filters.area !== "" || filters.q.trim() !== "";
}

export const CHANGE_HISTORY_PAGE_SIZE = 50;

/** The query string the server reads. A custom range with a missing date asks for the default range
 * instead, so a half-typed pair never reaches the server as an error. */
export function changeHistoryQuery(
  filters: ChangeHistoryFilters,
  extra: { cursor?: string; csv?: boolean; format?: GateExportFormat } = {},
): string {
  const params = new URLSearchParams();
  if (filters.range === "custom") {
    if (filters.from !== "" && filters.to !== "") {
      params.set("from", filters.from);
      params.set("to", filters.to);
    }
  } else {
    params.set("range", filters.range);
  }
  if (filters.person !== "") params.set("person", filters.person);
  if (filters.area !== "") params.set("area", filters.area);
  const q = filters.q.trim();
  if (q !== "") params.set("q", q);
  if (extra.format !== undefined || extra.csv === true) {
    params.set("format", extra.format ?? "csv");
  } else {
    params.set("limit", String(CHANGE_HISTORY_PAGE_SIZE));
    if (extra.cursor !== undefined) params.set("cursor", extra.cursor);
  }
  return params.toString();
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-09-07" as "Sep 7". */
export function shortDate(dateKey: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (match === null) return dateKey;
  return `${MONTHS[Number(match[2]) - 1] ?? match[2]} ${Number(match[3])}`;
}

/** "2026-09-14" as "Sep 14, 2026". */
export function longDate(dateKey: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (match === null) return dateKey;
  return `${shortDate(dateKey)}, ${match[1]}`;
}

/** The words after "212 changes" for the chosen range. */
export function rangeWords(
  filters: ChangeHistoryFilters,
  page: { from: string; to: string },
): string {
  switch (filters.range) {
    case "today":
      return "today";
    case "yesterday":
      return "yesterday";
    case "last_7_days":
      return "in the last 7 days";
    case "last_30_days":
      return "in the last 30 days";
    case "this_month":
      return "this month";
    case "custom":
      return `from ${shortDate(page.from)} to ${shortDate(page.to)}`;
  }
}

export function countLine(
  total: number | null,
  filters: ChangeHistoryFilters,
  page: { from: string; to: string },
): { count: string; rest: string } {
  const noun = (n: number): string => (n === 1 ? "change" : "changes");
  if (total === null) {
    return { count: "", rest: `Changes ${rangeWords(filters, page)}` };
  }
  if (filtersActive(filters)) {
    return { count: String(total), rest: `${noun(total)} match` };
  }
  return { count: String(total), rest: `${noun(total)} ${rangeWords(filters, page)}` };
}

export interface DayGroup {
  dayKey: string;
  heading: string;
  /** "Tue Oct 6 · 6 changes" */
  sub: string;
  entries: ChangeHistoryEntryDto[];
}

function addDays(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Day headings: Today and Yesterday by name, every other day by its own date. Entries stay in the
 * order the server sent them (newest first). */
export function groupByDay(
  entries: readonly ChangeHistoryEntryDto[],
  todayLocal: string,
): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    if (last !== undefined && last.dayKey === entry.dayKey) {
      last.entries.push(entry);
      continue;
    }
    const heading =
      entry.dayKey === todayLocal
        ? "Today"
        : entry.dayKey === addDays(todayLocal, -1)
          ? "Yesterday"
          : entry.dayLabel;
    groups.push({ dayKey: entry.dayKey, heading, sub: "", entries: [entry] });
  }
  for (const group of groups) {
    const first = group.entries[0];
    const count = group.entries.length;
    const words = `${count} ${count === 1 ? "change" : "changes"}`;
    // Today and Yesterday also say the date; a day headed by its own date does not repeat it.
    group.sub =
      group.heading === (first?.dayLabel ?? group.dayKey)
        ? words
        : `${first?.dayLabel ?? group.dayKey} \u00b7 ${words}`;
  }
  return groups;
}

export interface FilterPill {
  kind: "person" | "area" | "q";
  text: string;
}

export function filterPills(
  filters: ChangeHistoryFilters,
  people: readonly { key: string; name: string }[],
  areas: readonly { id: string; label: string }[],
): FilterPill[] {
  const pills: FilterPill[] = [];
  if (filters.person !== "") {
    pills.push({
      kind: "person",
      text: `Person: ${people.find((p) => p.key === filters.person)?.name ?? "Someone"}`,
    });
  }
  if (filters.area !== "") {
    pills.push({
      kind: "area",
      text: `Area: ${areas.find((a) => a.id === filters.area)?.label ?? filters.area}`,
    });
  }
  if (filters.q.trim() !== "") {
    pills.push({ kind: "q", text: `Search: ${filters.q.trim()}` });
  }
  return pills;
}

export interface EmptyCopy {
  kind: "first-use" | "filtered";
  title: string;
  body: string;
}

const RANGE_SENTENCE_END: Record<ChangeHistoryRangeId, string> = {
  today: "today",
  yesterday: "yesterday",
  last_7_days: "in the last 7 days",
  last_30_days: "in the last 30 days",
  this_month: "this month",
  custom: "in this date range",
};

/** What to say when the list is empty. "Nothing recorded yet" only when the server says nothing has ever
 * been recorded; otherwise the filters or the date range are why, and the words say which. */
export function emptyCopy(
  filters: ChangeHistoryFilters,
  page: Pick<ChangeHistoryPageDto, "historyStartsOn" | "people" | "areas">,
): EmptyCopy {
  if (page.historyStartsOn === null && !filtersActive(filters)) {
    return {
      kind: "first-use",
      title: "No changes yet",
      body: "When someone changes a setting, a price, a role or a PIN, it shows up here with their name and the time.",
    };
  }
  const when = RANGE_SENTENCE_END[filters.range];
  if (!filtersActive(filters)) {
    return {
      kind: "filtered",
      title: "No changes in this range",
      body: `Nothing was changed ${when}. Try a longer date range.`,
    };
  }
  const person =
    filters.person === "" ? undefined : page.people.find((p) => p.key === filters.person);
  const area = filters.area === "" ? undefined : page.areas.find((a) => a.id === filters.area);
  const who = person === undefined ? "Nobody" : person.name;
  const where = area === undefined ? "" : ` in ${area.label}`;
  const word = filters.q.trim() === "" ? "" : ` matching "${filters.q.trim()}"`;
  return {
    kind: "filtered",
    title: "No changes match these filters",
    body: `${who} has not changed anything${where}${word} ${when}. Try a longer date range, or clear a filter.`,
  };
}

/** The line at the foot of a list that is not the whole story. */
export function historyStartsText(page: Pick<ChangeHistoryPageDto, "historyStartsOn">): string {
  const base = "Changes are written as they happen and cannot be edited or deleted.";
  return page.historyStartsOn === null
    ? base
    : `${base} History starts ${longDate(page.historyStartsOn)}.`;
}

export function showingLine(shown: number, total: number | null): string {
  return total === null
    ? `Showing ${shown} changes`
    : `Showing ${shown} of ${total} ${total === 1 ? "change" : "changes"}`;
}

/** A sentence split around the search word so the word can be marked. Matching ignores case. */
export function markParts(text: string, q: string): { text: string; marked: boolean }[] {
  const needle = q.trim().toLowerCase();
  if (needle === "") return [{ text, marked: false }];
  const parts: { text: string; marked: boolean }[] = [];
  const lower = text.toLowerCase();
  let at = 0;
  for (;;) {
    const hit = lower.indexOf(needle, at);
    if (hit < 0) break;
    if (hit > at) parts.push({ text: text.slice(at, hit), marked: false });
    parts.push({ text: text.slice(hit, hit + needle.length), marked: true });
    at = hit + needle.length;
  }
  if (at < text.length) parts.push({ text: text.slice(at), marked: false });
  return parts.length === 0 ? [{ text, marked: false }] : parts;
}

/** The word a change is listed under when an entry shows items one by one. */
export function panelListHeading(entry: ChangeHistoryEntryDto): string {
  const n = entry.changes.length;
  if (entry.headline.tail !== null && n > 1 && entry.moreCount === 0) {
    return `${n} ${entry.headline.before.startsWith("Price list") ? "prices" : "changes"}, listed one by one`;
  }
  return n === 1 ? "What changed" : `${n} changes in this save`;
}
